/** @jsxImportSource @emotion/react */
import { useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import debounce from 'lodash/debounce'
import { useCallback, useMemo, useState } from 'react'
import { DEBOUNCE_DELAY, SEARCH_ITEMS_COUNT } from 'constants/consts'
import {
  findAllQuestion,
  findAllQuestion_findAllQuestion_items as Question,
  findAllQuestionVariables,
} from '__generatedTypes__/findAllQuestion'
import { QuestionLocationTypeEnum, QuestionWhere, SearchTypeEnum } from '__generatedTypes__/globalTypes'
import Autosuggest, { AutosuggestProps } from 'components/Autosuggest'
import Badge from 'components/Badge'
import { getFullPlatformTitle } from 'components/Layout/Sidebar/PlatformList'
import { GET_LIST_QUESTION } from 'queries/question'
import { colors } from 'theme/colors'
import * as classes from './styles'

export type QuestionPickerProps = Omit<
  AutosuggestProps<Question>,
  'items' | 'onInputChange' | 'loading' | 'changeImmediately' | 'multiple' | 'searchable'
> & {
  willUsedIn: QuestionLocationTypeEnum
  filter?: QuestionWhere
}

const defaultRenderQuestion: AutosuggestProps<Question>['renderItem'] = (question) => (
  <div css={classes.wrapper}>
    <Typography variant="body2" css={classes.questionTitle}>
      {question.title}
    </Typography>
    <div className="platforms-wrapper" css={classes.badge}>
      {question.alreadyInUse && <Badge css={classes.badgeInner} text="Already used" color={colors.red} />}
      {question.subjects.map(({ platform }) => (
        // TODO: how to resolve platform color?
        <Badge
          css={classes.badgeInner}
          key={platform.id}
          text={getFullPlatformTitle(platform.title) ?? platform.title}
          color={colors.blue}
        />
      ))}
    </div>
  </div>
)

export const QuestionPicker = ({
  filter,
  renderItem = defaultRenderQuestion,
  placeholder = 'Search Question',
  willUsedIn,
  ...props
}: QuestionPickerProps) => {
  const [search, setSearch] = useState('')

  const excludeIds = useMemo(() => {
    const { value } = props

    if (value) {
      return value?.map?.(({ id }) => id)
    }
  }, [props.value])

  const { data, loading } = useQuery<findAllQuestion, findAllQuestionVariables>(GET_LIST_QUESTION, {
    variables: {
      params: {
        take: SEARCH_ITEMS_COUNT,
        where: {
          isTemplate: false,
          excludeIds: excludeIds?.length ? excludeIds : undefined,
          willUsedIn,
          ...filter,
        },
        search: search ? { query: search, type: SearchTypeEnum.ILike } : undefined,
      },
    },
    fetchPolicy: 'network-only',
  })

  const handleQuestionInputChange = useCallback(
    debounce((searchString: string) => {
      setSearch(searchString)
    }, DEBOUNCE_DELAY),
    []
  )

  const items = data?.findAllQuestion.items ?? []

  const disabledItemIds = useMemo(() => items.filter(({ alreadyInUse }) => alreadyInUse).map(({ id }) => id), [items])

  return (
    <Autosuggest<Question>
      css={classes.search}
      popperCss={classes.withPlatformsStyles}
      {...props}
      items={items}
      onInputChange={handleQuestionInputChange}
      getItemLabel={(question) => question.title}
      renderItem={renderItem}
      placeholder={placeholder}
      loading={loading}
      changeImmediately
      multiple
      searchable
      disabledItemIds={disabledItemIds}
      data-testid="questions-picker"
    />
  )
}
